import { cloneDeep, get } from 'lodash-es';
import { emptyArray } from 'ui-components/default-props/empty';
import { PackageService } from '../../services/package-service';
import { FulfillmentPlanService } from '../../services/fulfillment-plan-service';

import Global from '../global-selectors';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';

export const EXTERNAL_SHIP_FROM_PREFIX = 'external**';

const prepareIncludeReturnLabelOption = (otherShippingOptions, serviceId, packages, state) => {
  if (!otherShippingOptions?.includeReturnLabel) {
    return otherShippingOptions;
  }

  const { includeReturnLabel } = otherShippingOptions;
  if (includeReturnLabel.serviceId && includeReturnLabel.packageTypeId && includeReturnLabel.labelProviderId) {
    return otherShippingOptions;
  }

  const defaultReturnServiceId = Global.selectSellerSellerSetting(state, {
    settingName: SellerSettings.returnService.key,
  });

  const defaultReturnPackageTypeArray =
    Global.selectSellerSellerSetting(state, {
      settingName: SellerSettings.returnPackage.key,
    })?.split('-') || emptyArray;

  const labelProviderId = Global.selectSellerServiceById(state, {
    serviceId,
  })?.labelProviderId;

  const safeOptions = cloneDeep(otherShippingOptions);

  const selectedPackageTypeId = safeOptions.includeReturnLabel.packageTypeId;
  const defaultReturnPackageTypeId = defaultReturnPackageTypeArray.length ? defaultReturnPackageTypeArray[0] : undefined;
  const CSWselectedPackageTypeId = packages?.length === 1 ? packages[0]?.packageTypeId : null;

  const actualPackageTypeId =
    selectedPackageTypeId || defaultReturnPackageTypeId || CSWselectedPackageTypeId || PackageService.getStandardPackageId();

  const otherOptionsSelectedServiceId = safeOptions.includeReturnLabel.serviceId;
  const actualServiceId = otherOptionsSelectedServiceId || defaultReturnServiceId || serviceId;

  const calculatedIncludeReturnLabelOption = {
    serviceId: actualServiceId,
    packageTypeId: actualPackageTypeId,
    labelProviderId: safeOptions.includeReturnLabel.labelProviderId,
  };
  if (!safeOptions.includeReturnLabel.labelProviderId) {
    calculatedIncludeReturnLabelOption.labelProviderId = labelProviderId;
  }

  safeOptions.includeReturnLabel = calculatedIncludeReturnLabelOption;

  return safeOptions;
};

const prepareConfirmationOption = (otherShippingOptions, serviceId, state) => {
  if (!serviceId) {
    return otherShippingOptions;
  }

  const service = Global.selectSellerServiceById(state, { serviceId });
  const carrierId = service?.carrierId;
  const carrierConfirmationTypes = Global.selectReferenceDataCarrierConfirmationsById(state, { carrierId });
  if (!carrierConfirmationTypes || carrierConfirmationTypes.length !== 1) {
    return otherShippingOptions;
  }

  const safeOptions = cloneDeep(otherShippingOptions);
  const { confirmationId } = carrierConfirmationTypes[0];
  safeOptions.confirmation = confirmationId;
  safeOptions.confirmationEnumId = confirmationId;
  return safeOptions;
};
const prepareOtherShippingOptions = (otherShippingOptions, serviceId, packages, state) => {
  const optionsWithCalculatedReturnLabel = prepareIncludeReturnLabelOption(otherShippingOptions, serviceId, packages, state);

  return prepareConfirmationOption(optionsWithCalculatedReturnLabel, serviceId, state);
};
const buildOtherShipmentOptions = (options, serviceId, packages, state) => {
  const built = {};

  if (!options) {
    return built;
  }

  Object.keys(options)
    .filter((option) => options[option] || options[option] === 0)
    .forEach((option) => (built[option] = options[option]));

  return prepareOtherShippingOptions(built, serviceId, packages, state);
};

export const getSellerFulfillmentProviderIdFromShipFromId = (shipFromId) => shipFromId.slice(10, shipFromId.length);

export const isShipFromExternal = (shipFromId) => !!shipFromId && `${shipFromId}`.includes(EXTERNAL_SHIP_FROM_PREFIX);

const buildUpdatePayloadForExternalShipment = (fulfillmentPlan) => {
  const externalFulfillmentConfiguration = get(fulfillmentPlan, 'externalFulfillmentConfiguration');

  const updatePayload = {
    shipFromId: `${EXTERNAL_SHIP_FROM_PREFIX}${get(externalFulfillmentConfiguration, 'sellerFulfillmentProviderId')}`,
    fulfillmentServiceId: get(externalFulfillmentConfiguration, 'fulfillmentServiceId'),
    shipTo: get(externalFulfillmentConfiguration, 'shipTo'),
    options: get(externalFulfillmentConfiguration, 'additionalOptions'),
  };

  return updatePayload;
};

export const buildCustoms = (needsCustoms, customs) => {
  // if we don't need customs, then we should set it to null to delete any old/erroneous customs data (which should occur on saveconfigandrate)
  return needsCustoms ? customs : null;
};

export const fulfillmentPlanToCSW = (fulfillmentPlan, accountPackages, CSWShippingConfiguration, actionType, state) => {
  if (FulfillmentPlanService.isExternal(fulfillmentPlan)) {
    return buildUpdatePayloadForExternalShipment(fulfillmentPlan);
  }
  const keys = Object.keys(fulfillmentPlan);
  const model = keys.reduce((obj, key) => {
    const fromCSW = get(CSWShippingConfiguration, `${key}`);
    const fromFP = get(fulfillmentPlan, `${key}`);

    return {
      ...obj,
      [key]: fromFP !== null && fromFP !== undefined ? fromFP : fromCSW,
    };
  }, {});

  const updatePayload = {
    serviceId: get(model, 'serviceId'),
    sellerLabelProviderId: get(model, 'sellerLabelProviderId'),
    packingSlipTemplateId: get(model, 'packingSlipTemplateId'),
    deliveryEmailNotificationTemplateId: get(model, 'deliveryEmailNotificationTemplateId'),
    shipmentEmailNotificationTemplateId: get(model, 'shipmentEmailNotificationTemplateId'),
    insuranceProvider: get(model, 'insuranceProvider'),
    properties: get(model, 'properties'),
    customs: buildCustoms(get(fulfillmentPlan, 'properties.needsCustoms', false), get(model, 'customs')),
    shipTo: get(fulfillmentPlan, 'shipTo'),
    shipDate: get(fulfillmentPlan, 'shipDate'),
    driverTip: get(fulfillmentPlan, 'labelConfiguration.driverTip'),
    availableShippingOptions: get(fulfillmentPlan, 'labelConfiguration.availableShippingOptions'),
    serviceCollectionGuid: fulfillmentPlan?.labelConfiguration?.serviceCollectionGuid ?? undefined,
  };

  const shipFromId = get(model, 'shipFromId');

  if (shipFromId != null) {
    updatePayload.shipFromId = shipFromId;
  }

  const packages = get(model, 'packages', emptyArray);
  if (!packages || packages.length === 0) {
    return updatePayload;
  }

  const singlePackage = packages[0];
  const realPackage = PackageService.findBestGuessPackageByPackageTypeId(singlePackage.packageTypeId, accountPackages);
  updatePayload.packages = packages;

  if (realPackage) {
    updatePayload.packages = packages.map((p) => ({
      ...p,
      packageId: `${get(p, 'packageTypeId', 'null')}-${get(realPackage, 'isCustom', 'false')}-${realPackage.carrierId || 'null'}`,
    }));
  }
  updatePayload.options = buildOtherShipmentOptions(get(model, 'options'), get(model, 'serviceId'), updatePayload.packages, state);

  return updatePayload;
};
